import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { CLP, UF, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card } from "@/components/ui/card";
import { CL_REGIONS, CL_REGION_NAMES } from "@/lib/clRegions";
import { HeroPhrasesAdmin } from "@/components/HeroPhrasesAdmin";
import { FeaturedMaestroAdmin } from "@/components/FeaturedMaestroAdmin";
import { HelpFeedbackAdmin } from "@/components/HelpFeedbackAdmin";
import { AdminReports } from "@/components/AdminReports";
import { FichasPie } from "@/components/FichasPie";
import { Flag, GraduationCap, Ticket, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  TrendingUp, Receipt, Wallet, Store, RefreshCw, Loader2, ShieldCheck, Landmark, Building2,
  Tag, Images, MessageSquareText, Plus, Trash2, ImagePlus, Percent, Settings2, AlertTriangle, Users,
} from "lucide-react";
import { AdminUsers } from "@/components/AdminUsers";
import { DiscountCampaigns } from "@/components/DiscountCampaigns";
import { InstagramManager } from "@/components/InstagramManager";
import { PlatformReset } from "@/components/PlatformReset";
import { useFees, fmtUF, reloadFees } from "@/lib/useFees";
import { FeeHistory } from "@/components/FeeHistory";
import { SectionMobileNav, ADMIN_TABS, DesktopMoreMenu } from "@/components/MaestroMobileNav";
import { AdminMessages } from "@/components/AdminMessages";
import { MessagesSquare } from "lucide-react";

const ADMIN_MAIN = ["metricas", "negocios", "usuarios", "mensajes", "denuncias"];

export default function AdminDashboard() {
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ADMIN_TABS.some((x) => x.value === t) ? t : "metricas";
  });
  const current = ADMIN_TABS.find((t) => t.value === tab);
  const [openReports, setOpenReports] = useState(0);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  useEffect(() => {
    const load = () => api.get("/admin/messages/threads").then((r) => setUnreadMsgs(r.data.unread)).catch(() => {});
    load();
    window.addEventListener("vt-admin-msgs", load);
    return () => window.removeEventListener("vt-admin-msgs", load);
  }, []);
  useEffect(() => {
    api.get("/admin/reports").then((r) => setOpenReports(r.data.filter((x) => x.status === "open").length)).catch(() => {});
  }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary text-primary-foreground"><ShieldCheck className="w-6 h-6" /></span>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Panel de Administración</h1>
          <p className="text-sm text-muted-foreground">Maestros, alumnos, Fichas y configuración de VIVOTaller</p>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <div className="lg:hidden flex items-center gap-2 mb-2" data-testid="admin-mobile-section-title">
          <current.icon className="w-5 h-5 text-primary" /><h2 className="text-lg font-bold">{current.label}</h2>
        </div>
        <div className="hidden lg:flex items-center justify-between gap-3">
        <TabsList className="flex flex-wrap h-auto justify-start">
          <TabsTrigger value="metricas" data-testid="admin-tab-metricas"><TrendingUp className="w-4 h-4 mr-1.5" />Métricas</TabsTrigger>
          <TabsTrigger value="negocios" data-testid="admin-tab-negocios"><Building2 className="w-4 h-4 mr-1.5" />Maestros</TabsTrigger>
          <TabsTrigger value="usuarios" data-testid="admin-tab-usuarios"><Users className="w-4 h-4 mr-1.5" />Usuarios</TabsTrigger>
          <TabsTrigger value="mensajes" data-testid="admin-tab-mensajes"><MessagesSquare className="w-4 h-4 mr-1.5" />Mensajes
            {unreadMsgs > 0 && <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-bold grid place-items-center" data-testid="admin-tab-mensajes-badge">{unreadMsgs}</span>}
          </TabsTrigger>
          <TabsTrigger value="denuncias" data-testid="admin-tab-denuncias"><Flag className="w-4 h-4 mr-1.5" />Denuncias
            {openReports > 0 && <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-bold grid place-items-center" data-testid="admin-tab-denuncias-badge">{openReports}</span>}
          </TabsTrigger>
        </TabsList>
        <DesktopMoreMenu tabs={ADMIN_TABS} main={ADMIN_MAIN} value={tab} onChange={setTab} testId="admin-more-menu" itemPrefix="admin-tab-" />
        </div>
        <TabsContent value="metricas"><Metricas /></TabsContent>
        <TabsContent value="uf"><UfPanel /></TabsContent>
        <TabsContent value="negocios"><Negocios /></TabsContent>
        <TabsContent value="usuarios"><AdminUsers /></TabsContent>
        <TabsContent value="mensajes"><AdminMessages onCount={setUnreadMsgs} /></TabsContent>
        <TabsContent value="categorias"><Categorias /></TabsContent>
        <TabsContent value="comunidad"><Comunidad /></TabsContent>
        <TabsContent value="configuracion"><Configuracion /></TabsContent>
        <TabsContent value="denuncias"><AdminReports onCount={setOpenReports} /></TabsContent>
      </Tabs>
      <SectionMobileNav tabs={ADMIN_TABS} value={tab} onChange={setTab} testId="admin-bottom-nav" badges={{ denuncias: openReports, mensajes: unreadMsgs }} />
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-primary" />}
      </div>
      <p className="font-display font-extrabold text-2xl mt-2">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </Card>
  );
}

function Metricas() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)); }, []);
  if (!m) return <Loader />;
  return (
    <div className="pt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-metrics">
      <Stat label="Maestros con cuenta" value={m.maestro_accounts} sub="Con y sin talleres publicados" icon={Users} />
      <Stat label="Maestros activos" value={m.active_maestros} sub="Con talleres visibles" icon={Store} />
      <Stat label="Alumnos" value={m.students_count} sub="Solo perfil alumno" icon={GraduationCap} />
      <Stat label="Alumnos que han reservado" value={m.students_booked} sub={m.students_count ? `${Math.round((m.students_booked / Math.max(m.students_count, m.students_booked)) * 100)}% de los alumnos` : ""} icon={Ticket} />
      <Stat label="Valor UF hoy" value={CLP(m.uf_value)} sub={m.uf_date} icon={Landmark} />
      <div className="sm:col-span-2 lg:col-span-5 grid"><FichasPie packs={m.packs} total={m.fichas_paid_clp} orders={m.fichas_orders} /></div>
    </div>
  );
}

function UfPanel() {
  const [data, setData] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/uf").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const sync = async () => { setBusy(true); try { await api.post("/admin/uf/sync"); toast.success("UF sincronizada desde mindicador.cl"); load(); } finally { setBusy(false); } };
  const override = async () => { if (!val) return; await api.post("/admin/uf/override", { valor: Number(val) }); toast.success("UF actualizada manualmente"); setVal(""); load(); };
  if (!data) return <Loader />;
  return (
    <div className="pt-6 space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Valor UF actual ({data.current.date})</p>
            <p className="font-display font-extrabold text-4xl text-primary mt-1">{CLP(data.current.valor)}</p>
            <p className="text-xs text-muted-foreground mt-1">Fuente: {data.current.source}</p>
          </div>
          <Button onClick={sync} disabled={busy} data-testid="uf-sync-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}Sincronizar ahora
          </Button>
        </div>
        <div className="flex gap-2 items-end mt-6 max-w-sm">
          <div className="flex-1"><label className="text-sm">Override manual (CLP)</label>
            <Input type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder="41065.38" data-testid="uf-override-input" /></div>
          <Button variant="outline" onClick={override} data-testid="uf-override-btn">Aplicar</Button>
        </div>
      </Card>
      <Card className="overflow-hidden">
        <div className="p-4 font-semibold text-sm">Historial reciente</div>
        <Table>
          <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Valor</TableHead><TableHead>Fuente</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.history.map((h) => (
              <TableRow key={h.date}><TableCell>{h.date}</TableCell><TableCell className="font-mono">{CLP(h.valor)}</TableCell><TableCell className="text-xs text-muted-foreground">{h.source}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function PublishingToggle({ b, onSaved }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const apply = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/businesses/${b.id}/publishing`, { enabled });
      toast.success(enabled
        ? `${b.name} puede publicar nuevamente${data.workshops_affected ? ` · ${data.workshops_affected} taller(es) republicados` : ""}${data.email_sent ? " · aviso enviado por correo" : ""}`
        : `${b.name} suspendido · ${data.workshops_affected} taller(es) ocultos del catálogo${data.email_sent ? " · aviso enviado por correo" : ""}`);
      setConfirm(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <Switch checked={b.can_publish} disabled={busy} data-testid={`publishing-switch-${b.id}`}
        className="data-[state=checked]:bg-emerald-600"
        onCheckedChange={(v) => (v ? apply(true) : setConfirm(true))} />
      <span className={`text-xs font-medium ${b.can_publish ? "text-emerald-700" : "text-muted-foreground"}`} data-testid={`publishing-status-${b.id}`}>
        {b.can_publish ? "Activo" : "Suspendido"}
      </span>
      <AlertDialog open={confirm} onOpenChange={(o) => !busy && setConfirm(o)}>
        <AlertDialogContent data-testid="suspend-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Confirmar Suspensión de Permisos de Publicación</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>¿Estás seguro de deshabilitar a <strong className="text-foreground">{b.name}</strong>?</p>
                <p>Al apagar este permiso:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>El Maestro no podrá crear ni publicar nuevos talleres.</li>
                  <li>Se ocultarán automáticamente del catálogo todos sus talleres activos, en proceso y futuros.</li>
                  <li>Las reservas existentes se conservan para su gestión.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} disabled={busy} data-testid="suspend-cancel">Cancelar</Button>
            <Button variant="destructive" onClick={() => apply(false)} disabled={busy} data-testid="suspend-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar y Deshabilitar"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function OrphanDelete({ b, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/businesses/${b.id}/orphan`);
      toast.success(`Perfil «${b.name}» eliminado · ${data.workshops_deleted} taller(es) quitados`);
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="ghost" className="mt-1 h-7 px-2 text-destructive hover:bg-destructive/10" onClick={() => setOpen(true)} data-testid={`biz-orphan-delete-${b.id}`}>
        <Trash2 className="w-3.5 h-3.5 mr-1" />Eliminar perfil
      </Button>
      <AlertDialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <AlertDialogContent data-testid="orphan-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" />Eliminar perfil sin cuenta</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Se eliminará el perfil <strong className="text-foreground">{b.name}</strong>, cuya cuenta dueña ya no existe, junto con sus talleres, sesiones, álbumes y cobros pendientes.</p>
                <p>Las reservas y pagos históricos se conservan. No se puede eliminar si tiene alumnos con sesiones por venir.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="orphan-delete-cancel">Cancelar</Button>
            <Button variant="destructive" onClick={run} disabled={busy} data-testid="orphan-delete-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Eliminar definitivamente"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function MaestroFilters({ q, setQ, region, setRegion, commune, setCommune, count }) {
  const communes = region !== "all" ? CL_REGIONS[region] || [] : [];
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center mb-4" data-testid="maestro-filters">
      <div className="relative flex-1 min-w-0">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar maestro por nombre o correo" className="pl-9" data-testid="maestro-search-input" />
      </div>
      <Select value={region} onValueChange={(v) => { setRegion(v); setCommune("all"); }}>
        <SelectTrigger className="sm:w-56" data-testid="maestro-region-select"><SelectValue placeholder="Región" /></SelectTrigger>
        <SelectContent><SelectItem value="all">Todas las regiones</SelectItem>{CL_REGION_NAMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={commune} onValueChange={setCommune} disabled={region === "all"}>
        <SelectTrigger className="sm:w-48" data-testid="maestro-commune-select"><SelectValue placeholder="Comuna" /></SelectTrigger>
        <SelectContent><SelectItem value="all">Todas las comunas</SelectItem>{communes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="maestro-filter-count">{count} maestro{count === 1 ? "" : "s"}</span>
    </div>
  );
}

function Negocios() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("all");
  const [commune, setCommune] = useState("all");
  const load = () => api.get("/admin/businesses").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const regionOf = (b) => b.region || Object.keys(CL_REGIONS).find((r) => CL_REGIONS[r].includes(b.commune));
  const shown = rows.filter((b) => (!q.trim() || norm(`${b.name} ${b.owner_email}`).includes(norm(q.trim())))
    && (region === "all" || regionOf(b) === region) && (commune === "all" || b.commune === commune));
  return (
    <div className="pt-6">
      <MaestroFilters q={q} setQ={setQ} region={region} setRegion={setRegion} commune={commune} setCommune={setCommune} count={shown.length} />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Maestro</TableHead><TableHead>Comuna</TableHead><TableHead>Incorporación</TableHead><TableHead>Talleres</TableHead>
            <TableHead>Pagado</TableHead><TableHead>Descuento packs</TableHead><TableHead>Permiso de publicación</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((b) => (
              <TableRow key={b.id} data-testid={`admin-business-${b.id}`}>
                <TableCell className="font-medium">
                  {b.name}
                  {b.owner_email && <p className="text-xs text-muted-foreground font-normal" data-testid={`biz-owner-${b.id}`}>{b.owner_email}</p>}
                  {b.orphaned && <Badge variant="destructive" className="mt-1" title="La cuenta dueña de este perfil ya no existe" data-testid={`biz-orphan-${b.id}`}>Sin cuenta</Badge>}
                  {b.orphaned && <div><OrphanDelete b={b} onDone={load} /></div>}
                  {b.pending_profile && <div><Badge variant="outline" className="mt-1 border-amber-400 text-amber-700 bg-amber-50" title="Creó su cuenta como Maestro pero aún no completa su perfil" data-testid={`biz-pending-${b.id}`}>Perfil sin completar</Badge></div>}
                </TableCell>
                <TableCell>{b.commune}{regionOf(b) && <p className="text-xs text-muted-foreground">{regionOf(b)}</p>}</TableCell>
                <TableCell className="whitespace-nowrap" data-testid={`biz-joined-${b.id}`}>{b.created_at ? new Date(b.created_at).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric", timeZone: "America/Santiago" }) : "—"}</TableCell>
                <TableCell>{b.workshops_count}</TableCell>
                <TableCell data-testid={`biz-paid-${b.id}`}>{CLP(b.paid_clp)}<p className="text-xs text-muted-foreground">{b.paid_orders} compra{b.paid_orders === 1 ? "" : "s"} de Fichas</p></TableCell>
                <TableCell>{b.pending_profile ? <span className="text-xs text-muted-foreground">—</span> : <DiscountEditor b={b} onSaved={load} />}</TableCell>
                <TableCell>{b.pending_profile ? <span className="text-xs text-muted-foreground">—</span> : <PublishingToggle b={b} onSaved={load} />}</TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground" data-testid="maestros-empty">{rows.length ? "Ningún maestro coincide con la búsqueda." : "Sin maestros."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function Categorias() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/admin/category-suggestions").then((r) => setSugs(r.data)).catch(() => {});
    return api.get("/admin/categories").then((r) => setRows(r.data));
  };
  const [sugs, setSugs] = useState([]);
  const resolve = async (id, action) => {
    try { const { data } = await api.post(`/admin/category-suggestions/${id}/${action}`); toast.success(action === "approve" ? `“${data.name}” agregada al listado` : "Sugerencia rechazada"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/admin/categories", { name: name.trim() }); toast.success("Categoría creada"); setName(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/categories/${id}`); toast.success("Categoría eliminada"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="pt-6 max-w-2xl space-y-5">
      <Card className="p-5">
        <h3 className="font-bold flex items-center gap-2 mb-1"><Tag className="w-5 h-5 text-primary" />Tipos de taller</h3>
        <p className="text-sm text-muted-foreground mb-4">Se usan en los filtros de exploración, las preferencias del Alumno y el formulario del Maestro.</p>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nueva categoría (ej. Repostería)"
            onKeyDown={(e) => e.key === "Enter" && add()} data-testid="category-name-input" />
          <Button onClick={add} disabled={busy || !name.trim()} data-testid="add-category-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Agregar</>}
          </Button>
        </div>
      </Card>
      {sugs.length > 0 && (
        <Card className="p-5" data-testid="category-suggestions">
          <h3 className="font-bold mb-3">Sugerencias de Maestros <Badge className="ml-1">{sugs.length}</Badge></h3>
          <div className="divide-y">
            {sugs.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 py-2" data-testid={`suggestion-${s.id}`}>
                <div className="flex-1 min-w-0"><p className="font-medium text-sm">{s.name}</p><p className="text-xs text-muted-foreground">{s.business_name}</p></div>
                <Button size="sm" onClick={() => resolve(s.id, "approve")} data-testid={`approve-suggestion-${s.id}`}>Aprobar</Button>
                <Button size="sm" variant="outline" onClick={() => resolve(s.id, "reject")} data-testid={`reject-suggestion-${s.id}`}>Rechazar</Button>
              </div>
            ))}
          </div>
        </Card>
      )}
      <Card className="p-5">
        <div className="flex flex-wrap gap-2" data-testid="categories-list">
          {rows.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 pl-3 pr-1.5 py-1 text-sm" data-testid={`category-row-${c.id}`}>
              {c.name}
              <button onClick={() => del(c.id)} className="grid place-items-center w-5 h-5 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" data-testid={`delete-category-${c.id}`} aria-label={`Eliminar ${c.name}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-2">Sin categorías aún.</p>}
        </div>
      </Card>
    </div>
  );
}

function PhotoStock({ kind, title, desc }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const base = `/admin/${kind}/photos`;
  const load = () => api.get(base).then((r) => setPhotos(r.data));
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const active = photos.filter((p) => p.active).length;
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`${base}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto subida"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const toggle = async (p) => { await api.patch(`${base}/${p.id}`, { active: !p.active }); load(); };
  const del = async (id) => { await api.delete(`${base}/${id}`); toast.success("Foto eliminada"); load(); };
  return (
    <Card className="p-5" data-testid={`${kind}-photos-card`}>
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="font-bold flex items-center gap-2"><Images className="w-5 h-5 text-primary" />{title}</h3>
        <label className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm cursor-pointer hover:bg-accent/40 transition-colors shrink-0" data-testid={`${kind}-upload-label`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Subir foto
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${kind}-upload-input`} />
        </label>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{desc} Activas: <strong data-testid={`${kind}-active-count`}>{active}</strong>.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${kind}-photos-grid`}>
        {photos.map((p) => (
          <div key={p.id} className={`relative rounded-xl overflow-hidden border aspect-[4/3] bg-muted ${p.active ? "ring-2 ring-primary" : "opacity-60"}`} data-testid={`${kind}-photo-${p.id}`}>
            <img src={fileUrl(p.url)} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-background/85 backdrop-blur px-2 py-1.5">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`toggle-${kind}-photo-${p.id}`} />
                {p.active ? "Activa" : "Oculta"}
              </label>
              <button onClick={() => del(p.id)} className="text-destructive" data-testid={`delete-${kind}-photo-${p.id}`} aria-label="Eliminar foto"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {photos.length === 0 && <p className="text-sm text-muted-foreground col-span-full py-2">Sin fotos aún.</p>}
      </div>
    </Card>
  );
}

function Comunidad() {
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const loadMessages = () => api.get("/admin/community/messages").then((r) => setMessages(r.data));
  useEffect(() => { loadMessages(); }, []);
  const activeMsgs = messages.filter((m) => m.active).length;
  const addMsg = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await api.post("/admin/community/messages", { text: msg.trim() }); toast.success("Mensaje agregado"); setMsg(""); loadMessages(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleMsg = async (m) => { await api.patch(`/admin/community/messages/${m.id}`, { active: !m.active }); loadMessages(); };
  const delMsg = async (id) => { await api.delete(`/admin/community/messages/${id}`); toast.success("Mensaje eliminado"); loadMessages(); };

  return (
    <div className="pt-6 grid lg:grid-cols-2 gap-6">
      <PhotoStock kind="community" title="Fotos de la comunidad" desc={<>La página muestra <strong>hasta 3</strong> al azar entre las activas, rotando en cada visita.</>} />
      <PhotoStock kind="hero" title="Fondo de portada" desc={<>Sello de agua detrás de «Vive un mundo de …». Se elige una al azar entre las activas <strong>solo al iniciar una nueva visita</strong>; nunca cambia mientras el usuario navega.</>} />
      <HeroPhrasesAdmin />
      <FeaturedMaestroAdmin />
      <HelpFeedbackAdmin />

      <Card className="p-5">
        <h3 className="font-bold flex items-center gap-2 mb-1"><MessageSquareText className="w-5 h-5 text-primary" />Mensajes de la comunidad</h3>
        <p className="text-sm text-muted-foreground mb-4">La página muestra <strong>uno por día</strong>, variando entre los activos. Activos: <strong>{activeMsgs}</strong>.</p>
        <div className="flex gap-2 mb-4">
          <Textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Escribe un mensaje cálido para la comunidad…" data-testid="message-input" />
          <Button onClick={addMsg} disabled={busy || !msg.trim()} className="shrink-0" data-testid="add-message-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Agregar</>}
          </Button>
        </div>
        <div className="space-y-2" data-testid="community-messages-list">
          {messages.map((m) => (
            <div key={m.id} className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${m.active ? "" : "opacity-60"}`} data-testid={`message-row-${m.id}`}>
              <p className="text-sm leading-relaxed">{m.text}</p>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={m.active} onCheckedChange={() => toggleMsg(m)} data-testid={`toggle-message-${m.id}`} />
                <button onClick={() => delMsg(m.id)} className="text-destructive" data-testid={`delete-message-${m.id}`} aria-label="Eliminar mensaje"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {messages.length === 0 && <p className="text-sm text-muted-foreground py-2">Sin mensajes aún.</p>}
        </div>
      </Card>
    </div>
  );
}

function DiscountEditor({ b, onSaved }) {
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(String(b.publication_discount_pct || 0));
  const [busy, setBusy] = useState(false);
  const base = b.publication_base_uf || 0.07;
  const finalUf = +(base * (1 - Number(pct) / 100)).toFixed(6);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/businesses/${b.id}/discount`, { pct: Number(pct) });
      toast.success("Descuento actualizado"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col items-start gap-1">
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`discount-btn-${b.id}`}>
          <Percent className="w-3.5 h-3.5 mr-1" />{b.publication_discount_pct || 0}%
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-3" align="end">
        <p className="text-sm font-semibold">Descuento en packs de Fichas</p>
        <p className="text-xs text-muted-foreground">Se aplica al valor del pack cuando el Maestro compra Fichas.</p>
        <Select value={pct} onValueChange={setPct}>
          <SelectTrigger data-testid={`discount-select-${b.id}`}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-60">
            {Array.from({ length: 21 }, (_, i) => i * 5).map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Tarifa base {UF(base)} UF → paga <strong className="text-foreground">{finalUf} UF</strong>{Number(pct) === 100 ? " (gratis)" : ""}
        </p>
        <Button className="w-full" size="sm" onClick={save} disabled={busy} data-testid={`discount-save-${b.id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Actualizar descuento"}
        </Button>
      </PopoverContent>
    </Popover>
    {b.discount_campaign_name && (
      <span className="text-[11px] font-semibold rounded-full bg-primary/10 text-primary px-2 py-0.5" title={`Hasta ${formatDate(`${b.discount_campaign_until}T12:00:00`)}`} data-testid={`discount-campaign-${b.id}`}>
        {b.discount_campaign_name}
      </span>
    )}
    </div>
  );
}

function Configuracion() {
  const [uf, setUf] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sess, setSess] = useState({ idle_minutes: 10, warning_minutes: 2 });
  const [histV, setHistV] = useState(0);
  useEffect(() => { api.get("/admin/settings").then((r) => { setUf(String(r.data.publication_fee_uf)); setSess(r.data.session); setLoaded(true); }); }, []);
  const saveSession = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/session", { idle_minutes: Number(sess.idle_minutes), warning_minutes: Number(sess.warning_minutes) });
      setSess(data); toast.success("Control de inactividad actualizado");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (!window.confirm(`¿Aplicar la nueva tarifa base de ${uf} UF? Regirá para todas las publicaciones futuras.`)) return;
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/publication-fee", { uf: Number(uf) });
      setUf(String(data.publication_fee_uf)); reloadFees().catch(() => {}); setHistV((v) => v + 1); toast.success("Tarifa base actualizada");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!loaded) return <Loader />;
  return (
    <div className="pt-6 max-w-2xl">
      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2"><Settings2 className="w-5 h-5 text-primary" /><h3 className="font-bold">Tarifa base de publicación</h3></div>
        <p className="text-sm text-muted-foreground">Costo que paga un Maestro sin descuento por publicar o copiar un taller. El nuevo valor aplica a publicaciones futuras.</p>
        <div>
          <Label>Tarifa base por taller (UF)</Label>
          <Input type="number" step="0.001" min="0" value={uf} onChange={(e) => setUf(e.target.value)} data-testid="publication-fee-input" />
        </div>
        <Button onClick={save} disabled={busy} data-testid="save-publication-fee-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar nueva tarifa base"}
        </Button>
      </Card>
      <FeeHistory version={histV} />
      <DiscountCampaigns />
      <InstagramManager />
      <Card className="p-6 space-y-4 mt-6" data-testid="session-settings-card">
        <div className="flex items-center gap-2"><Settings2 className="w-5 h-5 text-primary" /><h3 className="font-bold">Control de inactividad de sesión</h3></div>
        <p className="text-sm text-muted-foreground">Aplica a todos los perfiles (Super Admin, Maestro y Alumno). Tras el tiempo máximo sin actividad, la sesión se cierra automáticamente; antes se muestra un aviso con cuenta regresiva.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Tiempo máximo (min)</Label>
            <Input type="number" min="2" max="240" value={sess.idle_minutes} onChange={(e) => setSess({ ...sess, idle_minutes: e.target.value })} data-testid="session-idle-input" /></div>
          <div><Label>Aviso previo (min)</Label>
            <Input type="number" min="1" value={sess.warning_minutes} onChange={(e) => setSess({ ...sess, warning_minutes: e.target.value })} data-testid="session-warning-input" /></div>
        </div>
        <Button onClick={saveSession} disabled={busy} data-testid="save-session-settings-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar control de inactividad"}
        </Button>
      </Card>
      <PlatformReset />
    </div>
  );
}

function Loader() { return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>; }
